import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const fieldKeys = [
    'registration_date', 'family_name', 'given_name', 'family_name_kana',
    'given_name_kana', 'nationality', 'nationality_other', 'gender',
    'birth_date', 'age', 'residence_area', 'residence_country', 'postal_code', 'address',
    'address_detail', 'phone', 'email', 'contact_methods',
    'education', 'employment_status', 'transfer_timing', 'job_search_status',
    'preferred_location_1', 'preferred_location_2', 'preferred_location_3', 'desired_job_category',
    'desired_job', 'desired_job_other', 'current_salary', 'desired_salary',
    'employment_types', 'employment_other', 'experiences', 'language_skills',
    'pc_skills', 'pc_other', 'qualifications', 'transfer_reason',
    'self_pr', 'resume_later', 'privacy_consent', 'privacy_policy_version', 'contact_language',
];
// Form language codes used by the homepage form (api/_lib/jobseeker-languages.js).
const contactLanguages = { ja: '日本語', en: '英語', zh: '中国語（簡体字）', vi: 'ベトナム語', id: 'インドネシア語', ne: 'ネパール語', my: 'ミャンマー語', km: 'クメール語' };
const buttonClass = 'px-4 py-2 rounded-lg border border-[var(--border)] hover:bg-[var(--neutral-100)] disabled:opacity-50 disabled:cursor-not-allowed';
const hasValue = value => value !== null && value !== undefined && value !== '' && (!Array.isArray(value) || value.length > 0);
const text = value => Array.isArray(value) ? value.map(text).join('、') : (value && typeof value === 'object' ? JSON.stringify(value) : String(value ?? ''));
const formatDate = value => {
    const date = value ? new Date(value) : null;
    return date && !Number.isNaN(date.getTime()) ? date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }) : '---';
};

function FieldValue({ field, value, t }) {
    const missing = t('jobSeekers.missing');
    if (field === 'experiences' && Array.isArray(value)) {
        return value.map((entry, index) => {
            const experience = entry || {};
            return <div key={index} className="mb-3 space-y-1">
                <div>{t('jobSeekers.company')}：{text(experience.company) || missing}</div>
                <div>{t('jobSeekers.period')}：{text(experience.start) || missing} ～ {experience.current ? t('jobSeekers.current') : text(experience.end) || missing}</div>
                <div>{t('jobSeekers.employment')}：{text(experience.employment_type) || missing}</div>
                <div>{t('jobSeekers.position')}：{text(experience.position) || missing}</div>
                <div>{t('jobSeekers.duties')}：{text(experience.duties) || missing}</div>
            </div>;
        });
    }
    if (field === 'language_skills' && Array.isArray(value)) {
        return value.map((entry, index) => <div key={index}>{[
            entry?.language === 'その他' ? entry.other_language : entry?.language,
            entry?.certificate, entry?.level,
        ].map(part => text(part) || missing).join(' ／ ')}</div>);
    }
    if (field === 'age') return t('jobSeekers.age', { count: Number(value) });
    if (field === 'contact_language') return contactLanguages[value] || text(value);
    if (field === 'privacy_consent' && value === 'agreed') return t('jobSeekers.agreed');
    if (field === 'resume_later' && value === 'yes') return t('jobSeekers.later');
    if (['current_salary', 'desired_salary'].includes(field) && typeof value === 'string' && /^\d+(\.\d+)?$/.test(value.trim())) {
        return t('jobSeekers.salary', { value: value.trim() });
    }
    return text(value);
}

export function JobSeekerActionCard({ onClick }) {
    const { t } = useTranslation();
    return <button type="button" onClick={onClick} className="action-card action-card--teal">
        <div className="action-card-icon"><svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H5v15h14V5h-4M9 3h6v4H9zM8 12h8M8 16h6" />
        </svg></div>
        <span className="action-card-title">{t('jobSeekers.title')}</span>
        <span className="action-card-subtitle">{t('jobSeekers.description')}</span>
    </button>;
}

const memoMaxLength = 4000;
const emptyFollowUp = { contacted: false, contactedAt: null, contactedBy: null, memo: '', updatedAt: null, updatedBy: null, version: null };

// Same format the API accepts (JobSeekerStoragePaths.IsValidId).
const registrationIdPattern = /^[0-9]{13}-[a-f0-9]{8}-[a-f0-9]{4}-4[a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/;

export default function JobSeekerRegistrationsPanel({ initialRegistrationId } = {}) {
    const { t } = useTranslation();
    const { user } = useAuth();
    const allowed = user?.role === 'Admin' || user?.role === 'SuperAdmin';
    const [page, setPage] = useState(1);
    const [reload, setReload] = useState(0);
    const [result, setResult] = useState({ items: [], total: 0, pageSize: 20 });
    const [loading, setLoading] = useState(true);
    // A deep link (e.g. from the Lark notification card) opens that registration directly.
    const [selected, setSelected] = useState(() => (
        registrationIdPattern.test(initialRegistrationId || '') ? { id: initialRegistrationId } : null
    ));
    const keepInitialSelection = useRef(selected !== null);
    const selectedId = selected?.id;
    const [detail, setDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState('');
    const [status, setStatus] = useState('');
    const [busy, setBusy] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [memoDraft, setMemoDraft] = useState('');
    const [followUpMessage, setFollowUpMessage] = useState('');

    useEffect(() => {
        if (!allowed) return;
        let active = true;
        setLoading(true);
        setError('');
        if (keepInitialSelection.current) keepInitialSelection.current = false;
        else setSelected(null);
        apiService.getJobSeekerRegistrations({ page, pageSize: 20 }).then(data => {
            if (!active) return;
            const items = data.items || [];
            setResult({ items, total: Number.isFinite(data.total) ? data.total : null, pageSize: data.pageSize || 20 });
            // Upgrade a deep-linked selection to the list item so it shows in its table row.
            setSelected(previous => (previous && !previous.fullName && items.find(item => item.id === previous.id)) || previous);
        }).catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [allowed, page, reload]);

    useEffect(() => {
        setDetail(null);
        if (!allowed || !selectedId) return;
        let active = true;
        setDetailLoading(true);
        setError('');
        setShowAll(false);
        setFollowUpMessage('');
        apiService.getJobSeekerRegistration(selectedId).then(data => {
            if (!active) return;
            setDetail(data);
            setMemoDraft(data?.followUp?.memo || '');
        })
            .catch(err => { if (active) setError(err.message); })
            .finally(() => { if (active) setDetailLoading(false); });
        return () => { active = false; };
    }, [allowed, selectedId]);

    if (!allowed) return null;

    // Clicking the open row again closes it, so scanning several registrations stays quick.
    const toggle = item => { if (!busy) setSelected(previous => (previous?.id === item.id ? null : item)); };
    const notification = value => t(`jobSeekers.${value === true ? 'delivered' : value === false ? 'failed' : 'unknown'}`);
    const download = async kind => {
        setBusy(true);
        setError('');
        try {
            const { blob, fileName } = await apiService.downloadJobSeekerAttachment(selected.id, kind);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            try {
                link.href = url;
                link.download = fileName || detail.attachments.find(a => a.kind === kind)?.name || kind;
                document.body.appendChild(link);
                link.click();
            } finally {
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 0);
            }
        } catch (err) { setError(err.message); }
        finally { setBusy(false); }
    };
    const remove = async () => {
        if (!window.confirm(t('jobSeekers.confirm', { name: selected.fullName || detail?.fields?.full_name || t('jobSeekers.missing') }))) return;
        setBusy(true);
        setError('');
        setStatus('');
        try {
            await apiService.deleteJobSeekerRegistration(selected.id);
            setResult(previous => ({ ...previous, items: previous.items.filter(item => item.id !== selected.id), total: previous.total === null ? null : Math.max(0, previous.total - 1) }));
            setSelected(null);
            setStatus(t('jobSeekers.deleted'));
            if (result.items.length === 1 && page > 1) setPage(page - 1);
        } catch (err) { setError(err.message); }
        finally { setBusy(false); }
    };
    const fields = detail?.fields || {};
    const orderedKeys = [...fieldKeys, ...Object.keys(fields).filter(key => !fieldKeys.includes(key))];
    const followUp = detail?.followUp || emptyFollowUp;

    const saveFollowUp = async (changes, successKey) => {
        setBusy(true);
        setError('');
        setFollowUpMessage('');
        const id = selected.id;
        const applySaved = saved => {
            setDetail(previous => (previous?.id === id ? { ...previous, followUp: saved } : previous));
            setResult(previous => ({ ...previous, items: previous.items.map(item => (item.id === id
                ? { ...item, contacted: saved.contacted, hasMemo: Boolean(saved.memo) } : item)) }));
        };
        try {
            const saved = await apiService.updateJobSeekerFollowUp(id, {
                contacted: followUp.contacted, memo: followUp.memo, version: followUp.version, ...changes,
            });
            applySaved(saved);
            if ('memo' in changes) setMemoDraft(saved.memo);
            setFollowUpMessage(t(successKey));
        } catch (err) {
            if (err.status === 409 && err.data?.followUp) {
                // Another admin saved first: show their state, keep this admin's unsaved memo text.
                applySaved(err.data.followUp);
                setError(t('jobSeekers.followUp.conflict'));
            } else setError(err.message);
        } finally { setBusy(false); }
    };

    const join = (...values) => values.filter(hasValue).map(text).join(' ');
    const selectedName = selected?.fullName || fields.full_name || join(fields.family_name, fields.given_name);
    const selectedInList = Boolean(selected && result.items.some(item => item.id === selected.id));
    const residence = fields.residence_area === '海外'
        ? `海外（${text(fields.residence_country) || t('jobSeekers.missing')}）`
        : join(fields.postal_code ? `〒${text(fields.postal_code)}` : '', fields.address, fields.address_detail);
    const basicRows = [
        ['name', selectedName],
        ['kana', join(fields.family_name_kana, fields.given_name_kana)],
        ['fields.nationality', fields.nationality === 'その他' ? fields.nationality_other : fields.nationality],
        ['fields.gender', fields.gender],
        ['ageBirth', hasValue(fields.age) ? `${t('jobSeekers.age', { count: Number(fields.age) })}${fields.birth_date ? `（${text(fields.birth_date)}）` : ''}` : fields.birth_date],
        ['residence', residence],
        ['fields.phone', fields.phone],
        ['fields.email', fields.email],
        ['fields.contact_methods', fields.contact_methods],
        ['desiredJob', [fields.desired_job_category, fields.desired_job === 'その他' || fields.desired_job_category === 'その他' ? fields.desired_job_other : fields.desired_job].filter(hasValue).map(text).join(' ／ ')],
        ['preferredLocations', [fields.preferred_location_1, fields.preferred_location_2, fields.preferred_location_3].filter(hasValue).map(text).join('、')],
        ['fields.transfer_timing', fields.transfer_timing],
        ['fields.employment_status', fields.employment_status],
        ['fields.language_skills', hasValue(fields.language_skills) ? <FieldValue field="language_skills" value={fields.language_skills} t={t} /> : ''],
        ['fields.qualifications', fields.qualifications],
        ['fields.education', fields.education],
    ];
    const memoChanged = memoDraft !== followUp.memo;

    const detailPanel = selected && (
        <section aria-label={selectedName || t('jobSeekers.name')} className="p-4 md:p-6 bg-[var(--surface)] rounded-lg border border-[var(--border)] space-y-5">
            <div className="flex flex-wrap justify-between items-center gap-3">
                <h3 className="text-lg font-bold">{selectedName}</h3>
                <button type="button" className={buttonClass} disabled={busy} onClick={() => setSelected(null)}>{t('close')}</button>
            </div>
            {detailLoading && <p role="status">{t('loading')}</p>}
            {detail && <>
                <div className="flex flex-wrap items-center gap-3">
                    <button type="button" aria-pressed={followUp.contacted} disabled={busy}
                        className={followUp.contacted
                            ? 'px-4 py-2 rounded-lg border border-emerald-600 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50'
                            : 'px-4 py-2 rounded-lg border border-[var(--primary-600)] text-[var(--primary-600)] hover:bg-[var(--neutral-100)] disabled:opacity-50'}
                        onClick={() => saveFollowUp({ contacted: !followUp.contacted }, followUp.contacted ? 'jobSeekers.followUp.uncontactedSaved' : 'jobSeekers.followUp.contactedSaved')}>
                        {followUp.contacted ? `✓ ${t('jobSeekers.followUp.contacted')}` : t('jobSeekers.followUp.markContacted')}
                    </button>
                    {followUp.contacted
                        ? <span className="text-sm text-[var(--text-secondary)]">{t('jobSeekers.followUp.contactedInfo', { by: followUp.contactedBy || '---', at: formatDate(followUp.contactedAt) })}　{t('jobSeekers.followUp.undoHint')}</span>
                        : <span className="text-sm text-[var(--text-secondary)]">{t('jobSeekers.followUp.notContacted')}</span>}
                </div>

                <table className="w-full text-sm border border-[var(--border)] border-collapse" aria-label={t('jobSeekers.basicInfo')}>
                    <caption className="text-left font-bold pb-2">{t('jobSeekers.basicInfo')}</caption>
                    <tbody className="grid grid-cols-1 md:grid-cols-2">
                        {basicRows.map(([key, value]) => <tr key={key} className="flex border-b border-[var(--border)] md:odd:border-r">
                            <th scope="row" className="w-36 shrink-0 p-2 text-left font-normal text-[var(--text-secondary)] bg-[var(--neutral-100)]">{t(`jobSeekers.${key}`)}</th>
                            <td className="p-2 whitespace-pre-wrap break-words">{hasValue(value) ? (React.isValidElement(value) ? value : text(value)) : '---'}</td>
                        </tr>)}
                    </tbody>
                </table>

                <div className="space-y-2">
                    <label htmlFor={`memo-${selected.id}`} className="block font-bold">{t('jobSeekers.followUp.memo')}</label>
                    <textarea id={`memo-${selected.id}`} rows={4} maxLength={memoMaxLength} value={memoDraft} disabled={busy}
                        placeholder={t('jobSeekers.followUp.memoPlaceholder')} onChange={event => setMemoDraft(event.target.value)}
                        className="w-full p-3 rounded-lg border border-[var(--border)] bg-[var(--surface)] text-sm" />
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <button type="button" className={buttonClass} disabled={busy || !memoChanged} onClick={() => saveFollowUp({ memo: memoDraft }, 'jobSeekers.followUp.memoSaved')}>{t('jobSeekers.followUp.saveMemo')}</button>
                        <span className="text-[var(--text-secondary)]">{memoDraft.length} / {memoMaxLength}</span>
                        {followUp.updatedAt && <span className="text-[var(--text-secondary)]">{t('jobSeekers.followUp.lastUpdated', { by: followUp.updatedBy || '---', at: formatDate(followUp.updatedAt) })}</span>}
                        {memoChanged && <span className="text-amber-700">{t('jobSeekers.followUp.unsaved')}</span>}
                    </div>
                    <p role="status" className="text-sm text-emerald-700">{followUpMessage}</p>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button type="button" className={buttonClass} aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>
                        {showAll ? t('jobSeekers.hideAll') : t('jobSeekers.showAll')}
                    </button>
                    {['resume', 'history'].filter(kind => detail.attachments?.some(file => file.kind === kind)).map(kind => <button type="button" key={kind} className={buttonClass} disabled={busy} onClick={() => download(kind)}>{t(`jobSeekers.${kind}`)}</button>)}
                    <button type="button" className={`${buttonClass} text-red-700`} disabled={busy} onClick={remove}>{t('jobSeekers.delete')}</button>
                </div>

                {showAll && <section aria-label={t('jobSeekers.allInfo')}><dl className="space-y-3 pt-2 border-t border-[var(--border)]">
                    <div><dt className="text-sm text-[var(--text-secondary)]">{t('jobSeekers.submittedAt')}</dt><dd>{formatDate(detail.submittedAt)}</dd></div>
                    {orderedKeys.filter(key => hasValue(fields[key])).map(key => <div key={key}>
                        <dt className="text-sm text-[var(--text-secondary)]">{fieldKeys.includes(key) ? t(`jobSeekers.fields.${key}`) : key}</dt>
                        <dd className="whitespace-pre-wrap break-words"><FieldValue field={key} value={fields[key]} t={t} /></dd>
                    </div>)}
                    <div><dt>{t('jobSeekers.notification')}</dt><dd>{notification(detail.notificationDelivered)}</dd></div>
                    <div><dt>{t('jobSeekers.receipt')}</dt><dd>{notification(detail.receiptDelivered)}</dd></div>
                </dl></section>}
            </>}
        </section>
    );

    return <section aria-label={t('jobSeekers.title')} className="space-y-4 text-[var(--text-primary)]">
        <div className="flex flex-wrap justify-between items-center gap-3">
            <h2 className="text-xl font-bold">{t('jobSeekers.title')}</h2>
            <button type="button" className={buttonClass} disabled={loading || busy} onClick={() => setReload(value => value + 1)}>{t('refresh')}</button>
        </div>
        <p className="p-4 rounded-lg bg-[var(--surface)] border border-[var(--border)] text-sm">{t('jobSeekers.notice')}</p>
        <div role="status">{status}</div>
        {error && <p role="alert" className="text-red-700">{error}</p>}
        {!loading && !selectedInList && detailPanel}
        {loading ?<p role="status">{t('loading')}</p> : <>
            {!result.items.length ? <p>{t('jobSeekers.empty')}</p> : <div className="overflow-x-auto bg-[var(--surface)] border border-[var(--border)] rounded-lg">
                <table className="w-full text-sm text-left" aria-label={t('jobSeekers.listLabel')}>
                    <thead><tr>{['followUp.status', 'submittedAt', 'name', 'fields.nationality', 'fields.desired_job', 'fields.contact_methods', 'attachments', 'notification'].map(key => <th key={key} scope="col" className="p-3 whitespace-nowrap">{t(`jobSeekers.${key}`)}</th>)}</tr></thead>
                    <tbody>{result.items.map(item => <React.Fragment key={item.id}><tr className={`border-t border-[var(--border)] hover:bg-[var(--neutral-100)] cursor-pointer ${selected?.id === item.id ? 'bg-[var(--neutral-100)]' : ''}`} onClick={() => toggle(item)}>
                        <td className="p-3 whitespace-nowrap">
                            {item.contacted
                                ? <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800">{t('jobSeekers.followUp.contacted')}</span>
                                : <span className="inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800">{t('jobSeekers.followUp.notContactedShort')}</span>}
                            {item.hasMemo && <span className="ml-1 text-xs text-[var(--text-secondary)]">{t('jobSeekers.followUp.hasMemo')}</span>}
                        </td>
                        <td className="p-3 whitespace-nowrap">{formatDate(item.submittedAt)}</td>
                        <td className="p-3"><button type="button" className="text-[var(--primary-600)] underline whitespace-nowrap" disabled={busy} aria-expanded={selected?.id === item.id} onClick={event => { event.stopPropagation(); toggle(item); }}>{item.fullName || t('jobSeekers.missing')}</button></td>
                        <td className="p-3">{text(item.nationality) || '---'}</td>
                        <td className="p-3">{[item.desiredJobCategory, item.desiredJob].filter(hasValue).map(text).join(' ／ ') || '---'}</td>
                        <td className="p-3">{text(item.contactMethods) || '---'}</td>
                        <td className="p-3">{t('jobSeekers.documentCount', { count: item.attachments?.length || 0 })}</td>
                        <td className="p-3 whitespace-nowrap">{notification(item.notificationDelivered)}</td>
                    </tr>
                        {selected?.id === item.id && <tr><td colSpan={8} className="p-3">{detailPanel}</td></tr>}
                    </React.Fragment>)}</tbody>
                </table>
            </div>}
            <div className="flex items-center justify-center gap-4">
                <button type="button" className={buttonClass} disabled={page <= 1 || busy} onClick={() => setPage(page - 1)}>{t('jobSeekers.previous')}</button>
                <span>{result.total === null ? page : `${page} / ${Math.max(1, Math.ceil(result.total / result.pageSize))}`}</span>
                <button type="button" className={buttonClass} disabled={busy || (result.total === null ? result.items.length !== result.pageSize : page * result.pageSize >= result.total)} onClick={() => setPage(page + 1)}>{t('jobSeekers.next')}</button>
            </div>
        </>}

    </section>;
}
